import {
  Button,
  OutlinedSelect,
  SelectOptionType,
  TextOutlinedInput,
} from '@octano/global-ui';
import { ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import { AgreedPaymentsFilters } from '../types';

const dictPrefix = 'financialPortal.agreedPayments.searchControls';

interface FormValues {
  name: string;
  identificationTypeId: SelectOptionType | null;
  identification: string;
  studyPlanId: SelectOptionType | null;
}

const DEFAULTS: FormValues = {
  name: '',
  identificationTypeId: null,
  identification: '',
  studyPlanId: null,
};

interface Props {
  identificationTypes: SelectOptionType[];
  programs: SelectOptionType[];
  isDownloadLoading?: boolean;
  onSearch: (filters: AgreedPaymentsFilters) => void;
  onClear: () => void;
  onDownload: () => void;
}

export function SearchControls({
  identificationTypes,
  programs,
  isDownloadLoading = false,
  onSearch,
  onClear,
  onDownload,
}: Props): ReactElement {
  const { t } = useTranslation();
  const { handleSubmit, control, reset } = useForm<FormValues>({
    defaultValues: DEFAULTS,
  });

  const submit = (v: FormValues): void => {
    onSearch({
      name: v.name?.trim() || undefined,
      identificationTypeId:
        (v.identificationTypeId?.value as string) || undefined,
      identification: v.identification?.trim() || undefined,
      studyPlanId: (v.studyPlanId?.value as number) || undefined,
    });
  };

  const handleClear = (): void => {
    reset(DEFAULTS);
    onClear();
  };

  return (
    <Form onSubmit={handleSubmit(submit)}>
      <Row className="gutters-filters">
        <Col xs={12} md={3}>
          <TextOutlinedInput
            name="name"
            control={control}
            label={t(`${dictPrefix}.name.label`)}
            placeholder={t(`${dictPrefix}.name.placeholder`)}
          />
        </Col>
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="identificationTypeId"
            control={control}
            label={t(`${dictPrefix}.identificationType.label`)}
            placeholder={t(`${dictPrefix}.identificationType.placeholder`)}
            options={identificationTypes}
          />
        </Col>
        <Col xs={12} md={3}>
          <TextOutlinedInput
            name="identification"
            control={control}
            label={t(`${dictPrefix}.identification.label`)}
            placeholder={t(`${dictPrefix}.identification.placeholder`)}
          />
        </Col>
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="studyPlanId"
            control={control}
            label={t(`${dictPrefix}.program.label`)}
            placeholder={t(`${dictPrefix}.program.placeholder`)}
            options={programs}
            disabled={!programs.length}
          />
        </Col>
      </Row>
      <Row className="pt-0">
        <Col xs={12} md={{ size: 3, offset: 9 }}>
          <div className="d-flex" style={{ gap: 8 }}>
            <Button
              type="submit"
              text={t(`${dictPrefix}.search`)}
              size="sm"
              style={{
                height: 41,
                width: '100%',
              }}
            />
            <Button
              type="button"
              text={t(`${dictPrefix}.clear`)}
              size="sm"
              outlined
              onClick={handleClear}
              style={{
                height: 41,
                width: '100%',
              }}
            />
          </div>
        </Col>
      </Row>
      <Row className="pt-3 pb-4">
        <Col xs={12} md={{ size: 3, offset: 9 }}>
          <Button
            type="button"
            icon="download"
            text={t(`${dictPrefix}.download`)}
            size="sm"
            outlined
            loading={isDownloadLoading}
            onClick={onDownload}
            style={{
              width: '100%',
              height: 41,
            }}
          />
        </Col>
      </Row>
    </Form>
  );
}
